import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'odv-brick-usage';

/**
 * Hook for tracking brick usage frequency in localStorage
 *
 * Usage data structure:
 * {
 *   "assignments": { count: 45, lastAccess: 1735500000000 },
 *   "clients": { count: 32, lastAccess: 1735499000000 }
 * }
 */
export function useBrickUsage() {
  const [usageData, setUsageData] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Persist to localStorage whenever usage changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(usageData));
    } catch (err) {
      console.warn('[useBrickUsage] Failed to persist usage data:', err);
    }
  }, [usageData]);

  /**
   * Track a brick navigation
   */
  const trackUsage = useCallback((brickId) => {
    if (!brickId) return;

    setUsageData(prev => {
      const existing = prev[brickId] || { count: 0, lastAccess: 0 };
      return {
        ...prev,
        [brickId]: {
          count: existing.count + 1,
          lastAccess: Date.now()
        }
      };
    });
  }, []);

  /**
   * Get usage data for sorting
   */
  const getUsageData = useCallback(() => usageData, [usageData]);

  /**
   * Get bricks sorted by usage (most used first)
   */
  const getUsageRanking = useCallback(() => {
    return Object.entries(usageData)
      .sort((a, b) => b[1].count - a[1].count)
      .map(([id, data]) => ({ id, ...data }));
  }, [usageData]);

  /**
   * Get last N viewed bricks (most recent first)
   */
  const getRecentBricks = useCallback((limit = 10) => {
    return Object.entries(usageData)
      .filter(([, data]) => data.lastAccess > 0)
      .sort((a, b) => b[1].lastAccess - a[1].lastAccess)
      .slice(0, limit)
      .map(([id, data]) => ({ id, ...data }));
  }, [usageData]);

  /**
   * Clear all usage data
   */
  const clearUsage = useCallback(() => {
    setUsageData({});
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Get usage count for a specific brick
   */
  const getCount = useCallback((brickId) => {
    return usageData[brickId]?.count || 0;
  }, [usageData]);

  return {
    trackUsage,
    getUsageData,
    getUsageRanking,
    getRecentBricks,
    clearUsage,
    getCount,
    usageData
  };
}

export default useBrickUsage;
